import { ReactNode } from 'react';
import { ButtonColor, Theme, ValueOf } from '@redsift/design-system';
import { useTooltip } from './useTooltip';

/**
 * Context props.
 */
export type TooltipState =
  | (ReturnType<typeof useTooltip> & {
      /** Class name to append to the trigger. */
      readonly triggerClassName?: string;
    })
  | null;

/**
 * Component variant.
 */
export const TooltipPlacement = {
  top: 'top',
  right: 'right',
  bottom: 'bottom',
  left: 'left',
  'top-start': 'top-start',
  'top-end': 'top-end',
  'right-start': 'right-start',
  'right-end': 'right-end',
  'bottom-start': 'bottom-start',
  'bottom-end': 'bottom-end',
  'left-start': 'left-start',
  'left-end': 'left-end',
} as const;
export type TooltipPlacement = ValueOf<typeof TooltipPlacement>;

/**
 * Component props.
 */
export interface TooltipProps {
  /** Button color that will be forward to the trigger. */
  color?: ButtonColor;
  /** Children. Can only be TooltipTrigger and TooltilContent. */
  children: ReactNode;
  /**
   * Default open status.
   * Used for uncontrolled version.
   */
  defaultOpen?: boolean;
  /**
   * Delay time (in ms) for the tooltip to show up.
   * @default 300
   */
  delay?: number;
  /**
   * Default placement of the tooltip.
   * @default 'bottom'
   */
  placement?: TooltipPlacement | 'client-point';
  /**
   * Whether the component is opened or not.
   * Used for controlled version.
   */
  isOpen?: boolean;
  /**
   * Space between trigger and content (in pixels).
   * @default 8
   */
  offset?: number;
  /** Method to handle component change. */
  onOpen?: (open: boolean) => void;
  /** Id to the tooltip content. */
  tooltipId?: string;
  /** Theme. */
  theme?: Theme;
  /** Class name to append to the trigger. */
  triggerClassName?: string;
}

export type StyledTooltipProps = TooltipProps;
